import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'

type AuthMode = 'login' | 'signup'

export function LoginPage() {
  const [mode, setMode] = useState<AuthMode>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [signupConfirmation, setSignupConfirmation] = useState(false)
  const { signIn, signUp } = useAuth()
  const navigate = useNavigate()

  const handleModeSwitch = (newMode: AuthMode) => {
    setMode(newMode)
    setError('')
    setSignupConfirmation(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      if (mode === 'login') {
        await signIn(email, password)
        navigate('/dashboard')
      } else {
        await signUp(email, password, fullName)
        setSignupConfirmation(true)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : mode === 'login' ? 'Error al iniciar sesión' : 'Error al crear la cuenta')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-slate-800 mb-2">Content Engine</h1>
        </div>

        <div className="card">
          <div className="flex rounded-lg border border-slate-200 overflow-hidden text-sm mb-6">
            <button
              type="button"
              className={`flex-1 py-2 font-medium transition-colors ${
                mode === 'login'
                  ? 'bg-slate-900 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-50'
              }`}
              onClick={() => handleModeSwitch('login')}
            >
              Iniciar sesión
            </button>
            <button
              type="button"
              className={`flex-1 py-2 font-medium transition-colors ${
                mode === 'signup'
                  ? 'bg-slate-900 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-50'
              }`}
              onClick={() => handleModeSwitch('signup')}
            >
              Crear cuenta
            </button>
          </div>

          {signupConfirmation ? (
            <div className="text-center py-4 space-y-2">
              <p className="text-slate-800 font-medium">Revisa tu correo</p>
              <p className="text-slate-500 text-sm">
                Enviamos un enlace de confirmación a <span className="font-medium">{email}</span>.
                Confirma tu cuenta y luego inicia sesión.
              </p>
              <button
                type="button"
                className="mt-4 text-sm text-emerald-600 hover:underline"
                onClick={() => handleModeSwitch('login')}
              >
                Ir a iniciar sesión
              </button>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {mode === 'signup' && (
                  <div>
                    <label htmlFor="full-name" className="label">Nombre completo</label>
                    <input
                      id="full-name"
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="input"
                      placeholder="Tu nombre"
                      disabled={loading}
                      required
                    />
                  </div>
                )}

                <div>
                  <label htmlFor="email" className="label">Correo electrónico</label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input"
                    placeholder="tu@email.com"
                    disabled={loading}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="password" className="label">Contraseña</label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input"
                    placeholder="••••••••"
                    disabled={loading}
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn btn-primary py-3"
                >
                  {loading
                    ? mode === 'login' ? 'Iniciando sesión...' : 'Creando cuenta...'
                    : mode === 'login' ? 'Iniciar sesión' : 'Crear cuenta'}
                </button>
              </form>
            </>
          )}
        </div>

        <p className="mt-6 text-center text-slate-400 text-sm">
          Content Engine v0.1.0
        </p>
      </div>
    </div>
  )
}
